import { useTranslation } from 'react-i18next';
import { parseMarkdownContent } from '../../../../utils/parseMarkdownContent';
import ShareButtons from '../../../../components/common/ShareButtons';

interface ArticleContentProps {
  content: string;
  image: string;
  title: string;
}

export default function ArticleContent({ content, image, title }: ArticleContentProps) {
  const { t } = useTranslation();
  const html = parseMarkdownContent(content);

  return (
    <section className="bg-white py-16">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-12">
          <article>
            <div className="relative w-full h-72 md:h-96 rounded-2xl overflow-hidden mb-12 border border-primary/10">
              <img src={image} alt={title} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent" />
            </div>
            <div className="prose-web3" dangerouslySetInnerHTML={{ __html: html }} />
          </article>

          <aside className="space-y-6">
            <div className="bg-light-alt border border-primary/10 rounded-xl p-5 sticky top-24">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-list-check-2 text-primary text-sm" /></div>
                <span className="text-sm font-medium text-gray-800">{t('newsDetail.summary')}</span>
              </div>
              <div className="space-y-2">
                {content.split('\n').filter((l) => l.startsWith('## ')).map((l, i) => (
                  <div key={i} className="flex items-start gap-2 text-gray-500 text-sm py-1.5 px-2 rounded-lg hover:bg-white hover:text-primary transition-colors duration-200 cursor-pointer">
                    <span className="text-primary font-mono text-xs mt-0.5 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                    <span className="line-clamp-2">{l.replace('## ', '')}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-5 border-t border-primary/10">
                <p className="text-gray-400 text-xs mb-3">{t('newsDetail.contactHint')}</p>
                <a href="/contact" className="flex items-center justify-center gap-2 w-full py-2.5 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary-dark transition-colors duration-200 cursor-pointer whitespace-nowrap">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-customer-service-line text-sm" /></div>
                  {t('newsDetail.contactTeam')}
                </a>
              </div>
            </div>

            <div className="bg-light-alt border border-primary/10 rounded-xl p-5">
              <div className="text-sm font-medium text-gray-800 mb-3">{t('newsDetail.shareArticle')}</div>
              <ShareButtons
                variant="sidebar"
                channels={['twitter', 'linkedin', 'telegram', 'copy']}
                payload={{ title, text: title }}
              />
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
